import dayjs from 'dayjs';
import { RefinementCtx } from 'zod';

import {
  CohortBuilderDefinitionLegacy,
  IndexDateTypeLegacy,
  TimeWindow,
  TimeWindowDateTypeEnum,
  TimeWindowRelativeDateDirectionEnum,
} from '@verantos-dev/types-cohorts';

import { ValidationCodes } from './common';

type ValidationError = {
  path?: PropertyKey[] | undefined;
  customCode: ValidationCodes;
};

export const validateTimeWindow = (
  cohortDefinition: CohortBuilderDefinitionLegacy,
  timeWindow: TimeWindow
): { errors: ValidationError[] } => {
  const errors: ValidationError[] = [];

  if (!timeWindow || !timeWindow.startDate || !timeWindow.endDate) {
    return { errors };
  }

  const indexDateValue = cohortDefinition.indexDate?.indexDateValue;
  const indexDateType = cohortDefinition.indexDate?.type;
  const isStudyPeriodRestrictionEnabled =
    cohortDefinition.studyPeriodRestrictions?.overallStudyPeriodRestriction?.isEnabled;
  const studyPeriodStartDate =
    cohortDefinition.studyPeriodRestrictions?.overallStudyPeriodRestriction?.startDate;
  const studyPeriodEndDate =
    cohortDefinition.studyPeriodRestrictions?.overallStudyPeriodRestriction?.endDate;

  const { startDate, endDate } = timeWindow;

  // Scenario 1: Start date and end date are index date-based and the end date occurs before the start date
  if (
    startDate.type === TimeWindowDateTypeEnum.RELATIVE_TO_INDEX_DATE &&
    endDate.type === TimeWindowDateTypeEnum.RELATIVE_TO_INDEX_DATE &&
    startDate.relativeDate &&
    endDate.relativeDate
  ) {
    const startDays =
      startDate.relativeDate.direction === TimeWindowRelativeDateDirectionEnum.BEFORE
        ? -startDate.relativeDate.value
        : startDate.relativeDate.value;
    const endDays =
      endDate.relativeDate.direction === TimeWindowRelativeDateDirectionEnum.BEFORE
        ? -endDate.relativeDate.value
        : endDate.relativeDate.value;

    if (startDays > endDays) {
      errors.push({
        path: ['timeWindow', 'endDate', 'relativeDate', 'value'],
        customCode: ValidationCodes.TIME_WINDOW_START_AFTER_END,
      });
    }
  }

  // Scenario 2: Start date and end date are calendar date-based and the end date occurs before the start date
  if (
    startDate.type === TimeWindowDateTypeEnum.ABSOLUTE_DATE &&
    endDate.type === TimeWindowDateTypeEnum.ABSOLUTE_DATE &&
    startDate.date?.value &&
    endDate.date?.value
  ) {
    const startDateObj = dayjs.utc(startDate.date.value);
    const endDateObj = dayjs.utc(endDate.date.value);

    if (startDateObj.isAfter(endDateObj)) {
      errors.push({
        path: ['timeWindow', 'endDate', 'date', 'value'],
        customCode: ValidationCodes.TIME_WINDOW_START_AFTER_END,
      });
    }
  }

  // Scenario 3: Start date OR end date are calendar-date based and outside of the study period
  if (isStudyPeriodRestrictionEnabled && studyPeriodStartDate && studyPeriodEndDate) {
    if (startDate.type === TimeWindowDateTypeEnum.ABSOLUTE_DATE && startDate.date?.value) {
      const startDateObj = dayjs.utc(startDate.date.value);
      const studyStart = dayjs.utc(studyPeriodStartDate);
      const studyEnd = dayjs.utc(studyPeriodEndDate);

      if (startDateObj.isBefore(studyStart) || startDateObj.isAfter(studyEnd)) {
        errors.push({
          path: ['timeWindow', 'startDate', 'date', 'value'],
          customCode: ValidationCodes.TIME_WINDOW_OUTSIDE_STUDY_PERIOD,
        });
      }
    }

    if (endDate.type === TimeWindowDateTypeEnum.ABSOLUTE_DATE && endDate.date?.value) {
      const endDateObj = dayjs.utc(endDate.date.value);
      const studyStart = dayjs.utc(studyPeriodStartDate);
      const studyEnd = dayjs.utc(studyPeriodEndDate);

      if (endDateObj.isBefore(studyStart) || endDateObj.isAfter(studyEnd)) {
        errors.push({
          path: ['timeWindow', 'endDate', 'date', 'value'],
          customCode: ValidationCodes.TIME_WINDOW_OUTSIDE_STUDY_PERIOD,
        });
      }
    }
  }

  // Scenario 4: Index date is a calendar date, study period is set, and index date-based start or end date is outside of the study period
  if (
    isStudyPeriodRestrictionEnabled &&
    studyPeriodStartDate &&
    studyPeriodEndDate &&
    indexDateType === IndexDateTypeLegacy.CALENDAR &&
    indexDateValue
  ) {
    const indexDate = dayjs.utc(indexDateValue);
    const studyStart = dayjs.utc(studyPeriodStartDate);
    const studyEnd = dayjs.utc(studyPeriodEndDate);

    // Validate start date relative to index date
    if (
      startDate.type === TimeWindowDateTypeEnum.RELATIVE_TO_INDEX_DATE &&
      startDate.relativeDate
    ) {
      let calculatedStartDate: dayjs.Dayjs;

      if (startDate.relativeDate.direction === TimeWindowRelativeDateDirectionEnum.BEFORE) {
        calculatedStartDate = indexDate.subtract(startDate.relativeDate.value, 'days');
      } else {
        calculatedStartDate = indexDate.add(startDate.relativeDate.value, 'days');
      }

      if (calculatedStartDate.isBefore(studyStart) || calculatedStartDate.isAfter(studyEnd)) {
        errors.push({
          path: ['timeWindow', 'startDate', 'relativeDate', 'value'],
          customCode: ValidationCodes.TIME_WINDOW_OUTSIDE_STUDY_PERIOD,
        });
      }
    }

    // Validate end date relative to index date
    if (endDate.type === TimeWindowDateTypeEnum.RELATIVE_TO_INDEX_DATE && endDate.relativeDate) {
      let calculatedEndDate: dayjs.Dayjs;

      if (endDate.relativeDate.direction === TimeWindowRelativeDateDirectionEnum.BEFORE) {
        calculatedEndDate = indexDate.subtract(endDate.relativeDate.value, 'days');
      } else {
        calculatedEndDate = indexDate.add(endDate.relativeDate.value, 'days');
      }

      if (calculatedEndDate.isBefore(studyStart) || calculatedEndDate.isAfter(studyEnd)) {
        errors.push({
          path: ['timeWindow', 'endDate', 'relativeDate', 'value'],
          customCode: ValidationCodes.TIME_WINDOW_OUTSIDE_STUDY_PERIOD,
        });
      }
    }
  }

  return { errors };
};

export const validate = (cohortDefinition: CohortBuilderDefinitionLegacy, ctx: RefinementCtx) => {
  const subjectVariables = cohortDefinition.subjectVariables || [];
  subjectVariables.forEach((subjectVariable, index) => {
    const { errors } = validateTimeWindow(cohortDefinition, subjectVariable.timeWindow || {});
    errors.forEach((error) => {
      ctx.addIssue({
        code: 'custom',
        path: ['subjectVariables', index, ...(error.path ? error.path : [])],
        params: { customCode: error.customCode },
      });
    });
  });
};
